import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { appConfig } from './config';
import { devAccessToken } from './dev-hooks';

/**
 * OIDC authorization code + PKCE against the dev SSO, same library and settings
 * as the nurse app (oidc-client-ts, user stored per tab in sessionStorage).
 */
export const userManager = new UserManager({
  authority: appConfig.oidcIssuer,
  client_id: appConfig.oidcClientId,
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  scope: appConfig.oidcScope,
  response_type: 'code',
  loadUserInfo: true,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

/** Who is calling the media-token endpoint. */
export interface Principal {
  /** Bearer token for the Calls API. */
  accessToken: string;
  /** Base for the display name. */
  name: string;
  source: 'sso' | 'dev-hook';
}

interface ReturnState {
  returnTo?: string;
}

function fromUser(user: User): Principal {
  const name =
    (user.profile.name as string | undefined) ?? user.profile.preferred_username ?? user.profile.email ?? 'user';
  return { accessToken: user.access_token, name, source: 'sso' };
}

/**
 * Completes a /callback round trip, or returns the stored user, or starts a
 * redirect to the SSO (in which case the returned promise resolves to null
 * because the page navigates away).
 *
 * Dev server only: a token injected by the local test driver replaces sign-in
 * (see dev-hooks.ts). Production builds drop that branch.
 */
export async function ensureSignedIn(): Promise<Principal | null> {
  const injected = devAccessToken();
  if (injected) return { accessToken: injected, name: 'dev-hook', source: 'dev-hook' };

  if (window.location.pathname === '/callback') {
    const user = await userManager.signinRedirectCallback();
    const returnTo = (user.state as ReturnState | undefined)?.returnTo ?? '/';
    window.history.replaceState(null, '', returnTo);
    return fromUser(user);
  }
  const user = await userManager.getUser();
  if (user && !user.expired) return fromUser(user);
  await userManager.signinRedirect({
    state: { returnTo: window.location.pathname + window.location.search } satisfies ReturnState,
  });
  return null;
}

/** A current access token for a join, refreshed through the stored user when it has expired. */
export async function currentAccessToken(principal: Principal): Promise<string> {
  if (principal.source === 'dev-hook') return principal.accessToken;
  const user = await userManager.getUser();
  if (user && !user.expired) return user.access_token;
  const refreshed = await userManager.signinSilent().catch(() => null);
  if (refreshed) return refreshed.access_token;
  throw new Error('Session expired; reload to sign in again.');
}

export function signOut() {
  return userManager.signoutRedirect();
}
